import { useTranslation } from "react-i18next";
// src/pages/VendorList.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import EmptyState from "../components/EmptyState";
import {
  ListToolbar,
  type MenuItem,
  type SortOption,
} from "../components/ListToolbar";
import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { downloadCsv } from "../utils/downloadCsv";

import { A } from "../styles/appleClasses";
import { Button } from "../components/Button";
import ListTableShell from "../components/lists/ListTableShell";

interface Vendor {
  id: number;
  name: string;
  email?: string;
  mobile?: string;
}

const ROWS_PER_PAGE = 10;

type SortKey = "name" | "email" | "mobile";
type SortDir = "asc" | "desc";

/* =========================
   Refetch on focus/visibility
   ========================= */
function useRefetchOnFocus(refetch: () => void) {
  useEffect(() => {
    const onFocus = () => refetch();
    const onVis = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refetch]);
}

/* =========================
   CSV helpers
   ========================= */
function ymdTodayLocal() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function csvEscape(v: unknown) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

type DisplayError = { local: "loadFailed" } | { raw: string } | null;

export default function VendorList() {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    loadFailed: t("directory.vendors.loadFailed"),
  })[error.local];

  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id?: string }>();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DisplayError>(null);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = company_id
        ? `/vendors?company_id=${company_id}`
        : "/vendors";
      const url = `${base}${base.includes("?") ? "&" : "?"}_=${Date.now()}`;
      const data = await apiGet<Vendor[]>(url);
      setVendors(data || []);
    } catch {
      setError({ local: "loadFailed" });
    } finally {
      setLoading(false);
    }
  }, [company_id]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors, location.key, location.search]);

  useRefetchOnFocus(fetchVendors);

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir, vendors.length]);

  const clearFilters = () => {
    setSearch("");
    setSortKey("name");
    setSortDir("asc");
    setPage(1);
  };

  const filteredVendors = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return vendors;

    return vendors.filter((v) => {
      const name = (v.name || "").toLowerCase();
      const email = (v.email || "").toLowerCase();
      const mobile = (v.mobile || "").toLowerCase();
      return name.includes(q) || email.includes(q) || mobile.includes(q);
    });
  }, [vendors, search]);

  const sortedVendors = useMemo(() => {
    const list = filteredVendors.slice();
    const sign = sortDir === "asc" ? 1 : -1;

    const str = (s?: string) => (s || "").toLowerCase();
    const cmpStr = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }) * sign;

    list.sort((a, b) => {
      if (sortKey === "email") {
        const c = cmpStr(str(a.email), str(b.email));
        if (c !== 0) return c;
        return cmpStr(str(a.name), str(b.name));
      }

      if (sortKey === "mobile") {
        const c = cmpStr(str(a.mobile), str(b.mobile));
        if (c !== 0) return c;
        return cmpStr(str(a.name), str(b.name));
      }

      // sortKey === "name"
      const c = cmpStr(str(a.name), str(b.name));
      if (c !== 0) return c;

      // stable internal tie-breaker only
      return (a.id - b.id) * sign;
    });

    return list;
  }, [filteredVendors, sortKey, sortDir]);

  const totalFiltered = sortedVendors.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedVendors = sortedVendors.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );
  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const sortValue = `${sortKey}_${sortDir}`;
  const sortOptions: SortOption[] = [
    { label: t("directory.vendors.sortAsc"), value: "name_asc" },
    { label: t("directory.vendors.sortDesc"), value: "name_desc" },
    { label: t("directory.common.emailAsc"), value: "email_asc" },
    { label: t("directory.common.emailDesc"), value: "email_desc" },
    { label: t("directory.common.mobileAsc"), value: "mobile_asc" },
    { label: t("directory.common.mobileDesc"), value: "mobile_desc" },
  ];

  const onToolbarSortChange = (v: string) => {
    const [k, d] = v.split("_") as [SortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  const onHeaderSort = (k: SortKey) => {
    if (sortKey !== k) {
      setSortKey(k);
      setSortDir(k === "name" ? "asc" : "desc");
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const sortArrow = (k: SortKey) =>
    sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : null;

  const exportCsv = () => {
    const header = ["Name", "Email", "Mobile"];
    const rows = sortedVendors.map((v) => [
      v.name || "",
      v.email || "",
      v.mobile || "",
    ]);
    const csv =
      header.map(csvEscape).join(",") +
      "\n" +
      rows.map((r) => r.map(csvEscape).join(",")).join("\n");

    downloadCsv(`vendors_${ymdTodayLocal()}.csv`, csv);
  };

  const newItems: MenuItem[] = [
    {
      label: t("directory.vendors.new"),
      onClick: () => navigate(companyRoute(company_id, "/vendors/new")),
    },
  ];
  const moreItems: MenuItem[] = [{ label: t("directory.common.exportCsv"), onClick: exportCsv }];

  const iconBtn = [
    A.iconBtn,
    "inline-flex items-center justify-center",
    "hover:bg-gray-100",
    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
  ].join(" ");

  if (loading) {
    return (
      <div className={`min-h-screen bg-gray-50 ${A.body}`}>
        <div className="w-full max-w-5xl mx-auto px-2 sm:px-6 pt-4">
          <div className={`${A.card} p-6 text-center`}>{t("directory.vendors.loading")}</div>
        </div>
      </div>
    );
  }

  if (error) return <EmptyState heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} message={errorText(error) ?? ""} />;

  if (!vendors.length) {
    return (
      <EmptyState heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")}
        message={t("directory.vendors.empty")}
        showNewButton
        newButtonText={t("directory.vendors.newWithPlus")}
        newButtonPath={companyRoute(company_id, "/vendors/new")}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className="w-full max-w-5xl mx-auto px-2 sm:px-6 pt-4">
        {/* Header */}
        <div className="sticky top-0 z-30 bg-gray-50 border-b border-gray-100 px-2 pb-2 mb-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={iconBtn}
              aria-label={t("directory.common.back")}
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
            </button>

            <h2 className={`flex-1 text-center ${A.title}`}>{t("directory.vendors.title")}</h2>

            <button
              type="button"
              onClick={() => navigate(companyRoute(company_id, "/"))}
              className={iconBtn}
              aria-label={t("directory.common.close")}
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <ListToolbar moreLabel={t("directory.common.more")} sortLabel={t("directory.common.sort")}
          maxWidthClass="max-w-5xl"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("directory.vendors.search")}
          sortValue={sortValue}
          sortOptions={sortOptions}
          onSortChange={onToolbarSortChange}
          newItems={newItems}
          moreItems={moreItems}
          rightSummary={
            <div className="text-right leading-tight">
              <div className={A.label}>{t("directory.vendors.summary")}</div>
              <div
                className={`${A.num} text-[13px] font-semibold text-gray-900`}
              >
                {totalFiltered}
              </div>
            </div>
          }
        />

        {vendors.length > 0 && totalFiltered === 0 ? (
          <div className="px-2">
            <div className={`${A.card} p-6 text-center`}>
              {t("directory.common.noResults")}
              <button
                type="button"
                onClick={clearFilters}
                className="ml-2 text-[13px] font-normal text-appleblue hover:text-appleblueD focus:outline-none focus:ring-2 focus:ring-appleblueD rounded"
              >
                {t("directory.common.clearFilters")}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="block md:hidden space-y-3 px-2">
              {pagedVendors.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className={[
                    A.card,
                    "w-full text-left p-4 transition",
                    "hover:bg-gray-50",
                    "active:ring-2 active:ring-appleblue/20",
                    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                  ].join(" ")}
                  onClick={() =>
                    navigate(companyRoute(company_id, `/vendors/${v.id}`))
                  }
                  aria-label={t("directory.vendors.view", { name: v.name })}
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-gray-700 truncate">
                      {v.name || "—"}
                    </div>
                    {v.email ? (
                      <div className={`${A.label} truncate`}>{v.email}</div>
                    ) : null}
                    {v.mobile ? (
                      <div className={`${A.label} truncate`}>{v.mobile}</div>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block w-full max-w-5xl mx-auto px-2 mt-3">
              <ListTableShell>
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th
                        className="py-2 px-4 text-left text-xs text-gray-500 select-none cursor-pointer"
                        onClick={() => onHeaderSort("name")}
                        title={t("directory.vendors.sortVendor")}
                      >
                        {t("directory.vendors.singular")}{" "}
                        {sortArrow("name") ? (
                          <span className="text-gray-400">
                            {sortArrow("name")}
                          </span>
                        ) : null}
                      </th>
                      <th
                        className="py-2 px-2 text-left text-xs text-gray-500 select-none cursor-pointer"
                        onClick={() => onHeaderSort("email")}
                        title={t("directory.common.sortEmail")}
                      >
                        {t("directory.common.email")}{" "}
                        {sortArrow("email") ? (
                          <span className="text-gray-400">
                            {sortArrow("email")}
                          </span>
                        ) : null}
                      </th>
                      <th
                        className="py-2 px-2 text-left text-xs text-gray-500 select-none cursor-pointer"
                        onClick={() => onHeaderSort("mobile")}
                        title={t("directory.common.sortMobile")}
                      >
                        {t("directory.common.mobile")}{" "}
                        {sortArrow("mobile") ? (
                          <span className="text-gray-400">
                            {sortArrow("mobile")}
                          </span>
                        ) : null}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedVendors.map((v) => (
                      <tr
                        key={v.id}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition"
                        onClick={() =>
                          navigate(companyRoute(company_id, `/vendors/${v.id}`))
                        }
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            navigate(
                              companyRoute(company_id, `/vendors/${v.id}`),
                            );
                          }
                        }}
                        aria-label={`Vendor ${v.name}`}
                        style={{ outline: "none" }}
                      >
                        <td className="py-2 px-4">
                          <div className="font-medium text-gray-700">
                            {v.name || "—"}
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          {v.email ? (
                            <span>{v.email}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          {v.mobile ? (
                            <span>{v.mobile}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ListTableShell>

              <div className="flex justify-between items-center mt-2 px-1">
                <span className="text-gray-500 select-none">
                  {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
                </span>

                <div className="flex gap-3">
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {t("directory.common.prev")}
                  </Button>
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    disabled={page === totalPages || totalPages === 0}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {t("directory.common.next")}
                  </Button>
                </div>
              </div>
            </div>

            {/* Pagination (mobile) */}
            <div className="md:hidden flex justify-between items-center mt-4 px-2">
              <span className="text-gray-500 select-none">
                {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
              </span>

              <div className="flex gap-3">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t("directory.common.prev")}
                </Button>
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  disabled={page === totalPages || totalPages === 0}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
